import React, { useEffect, useState, useMemo } from 'react'
import { useMutation, useQueryClient, useQuery } from 'react-query';
import { apiService } from 'core/services/apiService';
import { Snackbar, Alert, CircularProgress, Box } from '@mui/material';
import { Edit } from '@mui/icons-material';
import { Button as MuiButton, Grid } from '@mui/material';
import styles from './GamemasterOptions.module.scss'; // Import SCSS module

interface GamemasterOptionsProps {
    onViewModeChange?: (mode: 'list' | 'edit') => void;
    canEdit?: boolean;
}

const GamemasterOptions: React.FC<GamemasterOptionsProps> = ({ onViewModeChange, canEdit = false }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [viewMode, setViewMode] = useState<'list' | 'edit'>('list');
    const [fillGaps, setFillGaps] = useState(false);
    const [triviaChannel, setTriviaChannel] = useState(false);
    const [trivia_channel_description, setChannelDescription] = useState('');
    const [trivia_channel_number, setChannelNumber] = useState<number | ''>(0);
    const [initialFillGaps, setInitialFillGaps] = useState(false);
    const [initialTriviaChannel, setInitialTriviaChannel] = useState(false);
    const [initialChannelDescription, setInitialChannelDescription] = useState('');
    const [initialChannelNumber, setInitialChannelNumber] = useState<number | ''>('');
    const [isLoadingSettings, setIsLoadingSettings] = useState(false);
    const [channelDescriptionError, setChannelDescriptionError] = useState<string | null>(null);
    const [channelNumberError, setChannelNumberError] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info' | 'warning';
    }>({
        open: false,
        message: '',
        severity: 'success',
    });
    const queryClient = useQueryClient();
    const workplaceId = Number(localStorage.getItem('workplace-id'));

    // Fetch settings using useQuery
    const fetchSettings = async () => {
        if (!workplaceId) return null;
        
        const res = await apiService.get(`/game_master_settings/${workplaceId}`);
        
        // Handle different response formats
        let data = res.data;
        // If data is wrapped in a member array (API Platform format)
        if (data?.member && Array.isArray(data.member) && data.member.length > 0) {
            data = data.member[0];
        }
        
        return data || null;
    };

    const { data: settingsData, isLoading: isSettingsLoading } = useQuery(
        ['gameMasterSettings', workplaceId],
        fetchSettings,
        {
            enabled: !!workplaceId,
            retry: false,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000, // 5 minutes
            onSuccess: (data) => {
                if (data) {
                    const fillGapsValue = Boolean(data.fillGapsWithNonEvents);
                    const triviaChannelValue = Boolean(data.triviaChannel);
                    const channelDescValue = data.triviaChannelDescription || '';
                    const rawNum = data.triviaChannelNumber;
                    const parsed = rawNum === '' || rawNum === null || typeof rawNum === 'undefined'
                        ? ''
                        : Math.max(0, Number(rawNum));
                    const channelNumValue = parsed === '' ? '' : Number(parsed);
                    
                    setFillGaps(fillGapsValue);
                    setTriviaChannel(triviaChannelValue);
                    setChannelDescription(channelDescValue);
                    setChannelNumber(channelNumValue);
                    
                    // Set initial values
                    setInitialFillGaps(fillGapsValue);
                    setInitialTriviaChannel(triviaChannelValue);
                    setInitialChannelDescription(channelDescValue);
                    setInitialChannelNumber(channelNumValue);
                } else {
                    // Defaults when no record
                    setFillGaps(false);
                    setTriviaChannel(false);
                    setChannelDescription('');
                    setChannelNumber('');
                    setInitialFillGaps(false);
                    setInitialTriviaChannel(false);
                    setInitialChannelDescription('');
                    setInitialChannelNumber('');
                }
            },
            onError: () => {
                // Defaults on error
                setFillGaps(false);
                setTriviaChannel(false);
                setChannelDescription('');
                setChannelNumber('');
                setInitialFillGaps(false);
                setInitialTriviaChannel(false);
                setInitialChannelDescription('');
                setInitialChannelNumber('');
            },
        }
    );

    useEffect(() => {
        setIsLoadingSettings(isSettingsLoading);
    }, [isSettingsLoading]);

    // Sync state with API response for table view
    useEffect(() => {
        if (settingsData) {
            const fillGapsValue = Boolean(settingsData.fillGapsWithNonEvents);
            const triviaChannelValue = Boolean(settingsData.triviaChannel);
            const channelDescValue = settingsData.triviaChannelDescription || '';
            const rawNum = settingsData.triviaChannelNumber;
            const parsed = rawNum === '' || rawNum === null || typeof rawNum === 'undefined'
                ? ''
                : Math.max(0, Number(rawNum));
            const channelNumValue = parsed === '' ? '' : Number(parsed);
            
            setFillGaps(fillGapsValue);
            setTriviaChannel(triviaChannelValue);
            setChannelDescription(channelDescValue);
            setChannelNumber(channelNumValue);
            
            // Update initial values when data changes
            setInitialFillGaps(fillGapsValue);
            setInitialTriviaChannel(triviaChannelValue);
            setInitialChannelDescription(channelDescValue);
            setInitialChannelNumber(channelNumValue);
        }
    }, [settingsData]);

    const { mutate: saveOptions, isLoading } = useMutation({
        mutationFn: async () => {
            // Reset errors
            setChannelDescriptionError(null);
            setChannelNumberError(null);

            // Validate if Trivia Channel is checked
            if (triviaChannel) {
                let hasError = false;

                // Validate Channel Description
                if (!trivia_channel_description || trivia_channel_description.trim() === '') {
                    setChannelDescriptionError('Channel Description is required');
                    hasError = true;
                }

                // Validate Channel Number
                if (trivia_channel_number === '' || trivia_channel_number === null || trivia_channel_number === undefined) {
                    setChannelNumberError('Channel Number is required');
                    hasError = true;
                }

                if (hasError) {
                    setSnackbar({
                        open: true,
                        message: 'Please fill in all required fields',
                        severity: 'error',
                    });
                    throw new Error('Validation failed');
                }
            }

            const payload = {
                workplace: `api/workplaces/${workplaceId}`,
                directTvEspnForBusiness: false,
                fillGapsWithNonEvents: Boolean(fillGaps),
                triviaChannel: Boolean(triviaChannel),
                // Clear description and number when Trivia is disabled
                triviaChannelDescription: triviaChannel ? (trivia_channel_description || '') : '',
                triviaChannelNumber: triviaChannel && trivia_channel_number !== '' 
                    ? String(trivia_channel_number) 
                    : '',
            };
            return apiService.post('/game_master_settings', payload);
        },
        onSuccess: async () => {
            // Update initial state to match current state after save
            setInitialFillGaps(fillGaps);
            setInitialTriviaChannel(triviaChannel);
            setInitialChannelDescription(trivia_channel_description);
            setInitialChannelNumber(trivia_channel_number);
            
            // Only invalidate specific query - useQuery will handle refetch automatically
            await queryClient.invalidateQueries(['gameMasterSettings', workplaceId]);
            setSnackbar({
                open: true,
                message: 'Options saved successfully',
                severity: 'success',
            });
            setViewMode('list');
            onViewModeChange?.('list');
        },
        onError: (error: any) => {
            // Don't show error snackbar for validation errors (already shown in mutationFn)
            if (error?.message === 'Validation failed') {
                return;
            }
            console.error('Failed to save Game Master options', error);
            setSnackbar({
                open: true,
                message: 'Failed to save options. Please try again.',
                severity: 'error',
            });
        },
    });

    // Settings are fetched via useQuery above - no need for separate useEffect

    const toggleAccordion = () => {
        setIsExpanded(!isExpanded);
    };

    const handleEditModeClick = () => {
        if (!canEdit) {
            return;
        }
        // Store initial state when entering edit mode
        setInitialFillGaps(fillGaps);
        setInitialTriviaChannel(triviaChannel);
        setInitialChannelDescription(trivia_channel_description);
        setInitialChannelNumber(trivia_channel_number);
        setViewMode('edit');
        onViewModeChange?.('edit');
    };

    const handleBackClick = () => {
        // Reset to initial state when going back without saving
        setFillGaps(initialFillGaps);
        setTriviaChannel(initialTriviaChannel);
        setChannelDescription(initialChannelDescription);
        setChannelNumber(initialChannelNumber);
        setChannelDescriptionError(null);
        setChannelNumberError(null);
        setViewMode('list');
        onViewModeChange?.('list');
    };

    // Check if changes were made
    const hasChanges = useMemo(() => {
        return fillGaps !== initialFillGaps ||
               triviaChannel !== initialTriviaChannel ||
               trivia_channel_description !== initialChannelDescription ||
               trivia_channel_number !== initialChannelNumber;
    }, [fillGaps, initialFillGaps, triviaChannel, initialTriviaChannel, 
        trivia_channel_description, initialChannelDescription, 
        trivia_channel_number, initialChannelNumber]);

    // Notify parent of initial viewMode
    useEffect(() => {
        onViewModeChange?.('list');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    switch (viewMode) {
        case 'edit':
            return (
                <>
                    <div className={styles.editOptionsSetting}>
                        <div className={styles.formContainer}>
                            <Grid container direction="column" spacing={2} pl={2}>
                                <Grid container direction="row" justifyContent="space-between">
                                    <Grid>
                                        <MuiButton className={styles.backButton} onClick={handleBackClick}>
                                            Back
                                        </MuiButton>
                                    </Grid>
                                    <Grid item xs={6} display={'flex'} justifyContent="flex-end">
                                        <Grid>
                                            <MuiButton
                                                className={styles.saveButton}
                                                onClick={() => saveOptions()}
                                                disabled={!canEdit || isLoading || !hasChanges}
                                                title={canEdit ? "Save" : "You do not have permission to edit"}
                                            >
                                                {isLoading ? 'Saving...' : 'Save'}
                                            </MuiButton>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid xs={12} container direction="row" justifyContent="flex-start" alignItems="center">
                                    <h2 className={styles.formTitle}>Edit Options</h2>
                                </Grid>
                            </Grid>

                            <form>
                                <div className={styles.checkboxGroup}>
                                    <label className={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            checked={Boolean(fillGaps)}
                                            onChange={(event) => {
                                                const fillGaps_checked = event.target.checked;
                                                setFillGaps(fillGaps_checked);
                                            }}
                                            className={styles.checkbox}
                                        />
                                        <span>Fill Gaps with Non Events</span>
                                    </label>

                                    <label className={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            checked={Boolean(triviaChannel)}
                                            onChange={(event) => {
                                                const triviaChannel_checked = event.target.checked;
                                                setTriviaChannel(triviaChannel_checked);
                                                // Clear channel description and number when Trivia is unchecked
                                                if (!triviaChannel_checked) {
                                                    setChannelDescription('');
                                                    setChannelNumber('');
                                                    setChannelDescriptionError(null);
                                                    setChannelNumberError(null);
                                                }
                                            }}
                                            className={styles.checkbox}
                                        />
                                        <span>Trivia Channel</span>
                                    </label>
                                </div>

                                {triviaChannel && (
                                    <div className={styles.inputGroup}>
                                        <label className={styles.inputLabel}>
                                            <span className={styles.labelText}>Channel Description:</span>
                                            <input
                                                type="text"
                                                value={trivia_channel_description}
                                                onChange={(e) => {
                                                    setChannelDescription(e.target.value);
                                                    if (channelDescriptionError) {
                                                        setChannelDescriptionError(null);
                                                    }
                                                }}
                                                className={`${styles.input} ${channelDescriptionError ? styles.inputError : ''}`}
                                            />
                                            {channelDescriptionError && (
                                                <span style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                                    {channelDescriptionError}
                                                </span>
                                            )}
                                        </label>
                                        <label className={styles.inputLabel}>
                                            <span className={styles.labelText}>Channel Number:</span>
                                            <input
                                                type="number"
                                                min={0}
                                                value={trivia_channel_number}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val === '') {
                                                        setChannelNumber('');
                                                    } else {
                                                        const n = Math.max(0, Number(val));
                                                        setChannelNumber(Number.isNaN(n) ? '' : n);
                                                    }
                                                    if (channelNumberError) {
                                                        setChannelNumberError(null);
                                                    }
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === '-' || e.key === 'e') {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                className={`${styles.input} ${channelNumberError ? styles.inputError : ''}`}
                                            />
                                            {channelNumberError && (
                                                <span style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                                    {channelNumberError}
                                                </span>
                                            )}
                                        </label>
                                    </div>
                                )}
                            </form>
                        </div>
                    </div>
                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={6000}
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert
                            onClose={() => setSnackbar({ ...snackbar, open: false })}
                            severity={snackbar.severity}
                            variant="filled"
                        >
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </>
            );

        case 'list':
        default:
            return (
                <>
                    <div style={{ width: '100%', margin: '10px 0', overflow: 'hidden', fontFamily: 'var(--Gotham)' }}>
                        <div className={styles.accordionHeader}>
                            <button onClick={toggleAccordion} style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                                <h5>Options</h5>
                            </button>
                            <button
                                className={styles.editButton}
                                onClick={handleEditModeClick}
                                title={canEdit ? "Edit Options" : "You do not have permission to edit"}
                                style={{ marginLeft: 'auto', opacity: canEdit ? 1 : 0.5, cursor: canEdit ? 'pointer' : 'not-allowed' }}
                                disabled={!canEdit}
                            >
                                <Edit className={styles.editIcon} />
                            </button>
                        </div>
                        <div
                            className={`${styles.accordionContent} ${isExpanded ? styles.expanded : styles.collapsed}`}
                        >
                            <div className={styles.content}>
                                <div className={styles.deviceCard}>
                                    <div className={styles.tableContainer}>
                                        <table className={styles.optionsTable}>
                                            <thead>
                                                <tr>
                                                    <th>Option</th>
                                                    <th>Value</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>Fill Gaps with Non Events</td>
                                                    <td>
                                                        {isSettingsLoading ? (
                                                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '10px' }}>
                                                                <CircularProgress size={25} />
                                                            </Box>
                                                        ) : (
                                                            (settingsData?.fillGapsWithNonEvents ?? fillGaps) ? 'Yes' : 'No'
                                                        )}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>Trivia Channel</td>
                                                    <td>
                                                        {isSettingsLoading ? (
                                                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '10px' }}>
                                                                <CircularProgress size={25} />
                                                            </Box>
                                                        ) : (
                                                            (settingsData?.triviaChannel ?? triviaChannel) ? 'Yes' : 'No'
                                                        )}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={6000}
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert
                            onClose={() => setSnackbar({ ...snackbar, open: false })}
                            severity={snackbar.severity}
                            variant="filled"
                        >
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </>
            );
    }
}

export default GamemasterOptions;